import { BaseScreenProps } from '@/features/quiz/types'
import { InferType, object, string } from 'yup'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import ScreenTitle from '@/features/quiz/screen-title'
import QuizTextInput from '@/features/quiz/inputs/quiz-text-input'
import BottomContinueButton from '@/features/quiz/bottom-continue-button'
import React, { useEffect, useRef, useState } from 'react'
import { AU_STATES } from '@/shared/forms/RHFProfileFormInputCollection'
import QuizDropdownInput from '@/features/quiz/inputs/quiz-dropdown-input'
import QuizGooglePlacesInput from '@/features/quiz/inputs/quiz-google-places-input'
import { useDebounce } from '@uidotdev/usehooks'
import SlideOutMenu from '@/features/quiz/slide-out-menu'
import { OTPInput, SlotProps } from 'input-otp'
import clsx from 'clsx'
import { formatDate, formatMobileNumber, parseDate } from '@/lib/utils'
import RoundedButton from '@/shared/components/buttons/rounded-button'
import useInterval from '@/shared/hooks/useInterval'
import { differenceInSeconds } from 'date-fns'
import { useMutation } from '@tanstack/react-query'
import { StartTfaChallenge, VerifyTfaChallenge } from '@/lib/apis/tfa'
import useApiClient from '@/lib/apis/clients/useApiClient'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { isHttpError } from '@/lib/apis/error'
import {
  billingDetailsSchema as schema,
  normalizeQuizMobile,
} from './billing-details-validation'
import { UpdateUserProfile } from '@/lib/apis/profile'
import type { PatientProfileForm } from '@/lib/apis/validators'

export type BillingDetailsScreenProps = {
  verifiedMobile: string
  setVerifiedMobile: React.Dispatch<React.SetStateAction<string>>

  allowSkippingMobileVerification?: boolean
} & BaseScreenProps

interface FormValues extends InferType<typeof schema> {}

const DEBOUNCE_VALUE = 300

export default function BillingDetailsScreen({
  screenId,
  onChange,
  verifiedMobile,
  setVerifiedMobile,
  answers,
  profile,

  allowSkippingMobileVerification = false,
}: BillingDetailsScreenProps) {
  const [verification, setVerification] = useState<{
    token: string
    values: FormValues
  } | null>(null)
  const [errorMessage, setErrorMessage] = useState('')
  const finishing = useRef(false)
  const errorRef = useRef<HTMLParagraphElement>(null)

  useEffect(() => {
    if (errorMessage && !errorMessage.startsWith('Please check')) {
      errorRef.current?.scrollIntoView({ block: 'center' })
    }
  }, [errorMessage])

  let answerForScreen: FormValues = schema.cast({})
  const currentAnswer = answers[screenId]

  if (currentAnswer && currentAnswer.type === 'billing-details') {
    if (currentAnswer.firstName.length > 0)
      answerForScreen.firstName = currentAnswer.firstName
    if (currentAnswer.lastName.length > 0)
      answerForScreen.lastName = currentAnswer.lastName
    if (currentAnswer.mobile.length > 0)
      answerForScreen.mobile = currentAnswer.mobile
    if (currentAnswer.formattedAddress.length > 0)
      answerForScreen.formattedAddress = currentAnswer.formattedAddress
    if (currentAnswer.suburb.length > 0)
      answerForScreen.suburb = currentAnswer.suburb
    if (currentAnswer.state.length > 0)
      answerForScreen.state = currentAnswer.state
    if (currentAnswer.postcode.length > 0)
      answerForScreen.postcode = currentAnswer.postcode
  }

  // Override the remaining answers with the previously filled profile if the
  // form value is empty
  if (profile) {
    if (answerForScreen.firstName.length === 0)
      answerForScreen.firstName = profile.firstName
    if (answerForScreen.lastName.length === 0)
      answerForScreen.lastName = profile.lastName
    if (answerForScreen.mobile.length === 0)
      answerForScreen.mobile = profile.mobile
    if (answerForScreen.formattedAddress.length === 0)
      answerForScreen.formattedAddress = profile.formattedAddress
    if (answerForScreen.suburb.length === 0)
      answerForScreen.suburb = profile.suburb
    if (answerForScreen.state.length === 0)
      answerForScreen.state = profile.state
    if (answerForScreen.postcode.length === 0)
      answerForScreen.postcode = profile.postcode
  }

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onTouched',
    defaultValues: answerForScreen,
    resolver: yupResolver(schema),
  })

  const client = useApiClient()
  const startTfaMutation = useMutation(StartTfaChallenge.mutation(client))
  const updateProfileMutation = useMutation(UpdateUserProfile.mutation(client))

  const { handleSubmit, formState, watch, setValue } = methods
  const { isSubmitting } = formState

  const formValues = watch()
  const {
    firstName,
    lastName,
    mobile,
    state,
    suburb,
    postcode,
    formattedAddress,
  } = formValues

  const debouncedFirstName = useDebounce(firstName, DEBOUNCE_VALUE)
  const debouncedLastName = useDebounce(lastName, DEBOUNCE_VALUE)
  const debouncedMobile = useDebounce(mobile, DEBOUNCE_VALUE)
  const debouncedState = useDebounce(state, DEBOUNCE_VALUE)
  const debouncedSuburb = useDebounce(suburb, DEBOUNCE_VALUE)
  const debouncedPostcode = useDebounce(postcode, DEBOUNCE_VALUE)
  const debouncedFormattedAddress = useDebounce(
    formattedAddress,
    DEBOUNCE_VALUE,
  )

  useEffect(() => {
    if (finishing.current) return
    const mobileToUpdate = normalizeQuizMobile(mobile)
    const mobileIsVerified =
      !!verifiedMobile && normalizeQuizMobile(verifiedMobile) === mobileToUpdate

    onChange(
      screenId,
      {
        id: screenId,
        type: 'billing-details',
        ...formValues,

        mobile: mobileToUpdate,
        mobileVerified: mobileIsVerified,
        completed: false,
      },
      false,
    )
      .then()
      .catch(console.error)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedFirstName,
    debouncedLastName,
    debouncedMobile,
    debouncedState,
    debouncedSuburb,
    debouncedPostcode,
    debouncedFormattedAddress,
    verifiedMobile,
  ])

  async function startMobileVerification(mobile: string) {
    const result = await startTfaMutation.mutateAsync({
      data: { channel: 'Sms', address: mobile, intent: 'PROFILE_UPDATE' },
    })
    return result.token
  }

  async function requestMobileVerification(values: FormValues) {
    try {
      const token = await startMobileVerification(values.mobile)
      setVerification({ token, values })
    } catch (error) {
      setErrorMessage(smsRequestError(error))
    }
  }

  async function onSubmit(values: FormValues) {
    setErrorMessage('')
    if (
      normalizeQuizMobile(verifiedMobile) !== values.mobile &&
      !allowSkippingMobileVerification
    ) {
      await requestMobileVerification(values)
      return
    }
    try {
      await completeContactDetails(values)
    } catch (error) {
      if (requiresMobileVerification(error)) {
        await requestMobileVerification(values)
        return
      }
      setErrorMessage(
        "We couldn't save your details. Your entries are still here. Please try Continue again.",
      )
    }
  }

  async function completeContactDetails(
    values: FormValues,
    verificationToken?: string,
  ) {
    if (finishing.current) return
    finishing.current = true
    try {
      await saveProfile(values, verificationToken)
      setVerifiedMobile(values.mobile)
      await onChange(
        screenId,
        {
          id: screenId,
          type: 'billing-details',
          ...values,
          mobileVerified: true,
          completed: true,
        },
        true,
      )
      // Keep draft autosaves paused until this screen unmounts.
    } catch (error) {
      finishing.current = false
      if (requiresMobileVerification(error)) {
        // A saved quiz flag is only a hint; the backend can require fresh proof.
        // Also persist its invalidation through the draft effect above.
        setVerifiedMobile('')
      }
      throw error
    }
  }

  async function saveProfile(
    values: FormValues,
    mobileVerificationToken?: string,
  ) {
    if (!profile) return
    const dobAnswer = answers['DateOfBirth']
    const genderAnswer = answers['assigned_sex_at_birth']
    const dob =
      dobAnswer?.type === 'text'
        ? formatDate(parseDate(dobAnswer.answer, 'dd-MM-yyyy'))
        : (profile.dob ?? '')
    const gender =
      genderAnswer?.type === 'single-choice'
        ? genderAnswer.answer === 'female'
          ? 'F'
          : 'M'
        : (profile.gender ?? 'M')

    const updatedProfile: PatientProfileForm = {
      ...values,
      email: profile.email,
      dob,
      gender,
      concessionNumber: profile.concessionNumber ?? '',
      entitlementNumber: profile.entitlementNumber ?? '',
      isAbOrTorIslander: profile.isAbOrTorIslander ?? false,
    }
    await updateProfileMutation.mutateAsync({
      profile: updatedProfile,
      mobileVerificationToken,
    })
  }

  return (
    <>
      <div className="mb-16 w-full">
        <div className="mx-auto max-w-3xl">
          <FormProvider {...methods}>
            <form
              className="flex flex-col gap-y-9 sm:gap-y-10 lg:gap-y-11"
              noValidate
              onSubmit={handleSubmit(onSubmit, () =>
                setErrorMessage('Please check the highlighted fields below.'),
              )}
            >
              <ScreenTitle
                title="Tell us a little bit more about yourself so we can connect you with the right practitioner."
                subtitle="We will deliver your treatment to this address if you choose to include delivery."
              />

              {errorMessage && (
                <p ref={errorRef} role="alert" className="text-sm text-red-600">
                  {errorMessage}
                </p>
              )}
              <div className="space-y-4 sm:space-y-5 lg:space-y-6">
                <QuizTextInput
                  name="firstName"
                  label="First name (legal)"
                  autoComplete="given-name"
                />
                <QuizTextInput
                  name="lastName"
                  label="Last name (legal)"
                  autoComplete="family-name"
                />
                <QuizGooglePlacesInput
                  name="formattedAddress"
                  label="Street address"
                  autoComplete="address-line1"
                  onPickAddress={(address) => {
                    setValue('formattedAddress', address.formattedAddress, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                    setValue('suburb', address.suburb, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                    setValue('state', address.state, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                    setValue('postcode', address.postcode, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    })
                  }}
                />
                <QuizTextInput
                  name="suburb"
                  label="Suburb"
                  autoComplete="address-level2"
                />
                <QuizDropdownInput
                  name="state"
                  label="State"
                  autoComplete="address-level1"
                  options={[
                    { id: '', label: '' },
                    ...AU_STATES.map((state) => ({ id: state, label: state })),
                  ]}
                />
                <QuizTextInput
                  name="postcode"
                  label="Postcode"
                  autoComplete="postal-code"
                  inputMode="numeric"
                />
                <QuizTextInput
                  name="mobile"
                  label="Mobile number"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                />
              </div>

              <div>
                <BottomContinueButton
                  initial={false}
                  show={true}
                  disabled={isSubmitting}
                  buttonProps={{
                    type: 'submit',
                    loading: isSubmitting,
                    'aria-label': 'Continue',
                    'aria-busy': isSubmitting,
                  }}
                />
              </div>
            </form>
          </FormProvider>
        </div>
      </div>
      <SlideOutMenu
        show={!!verification}
        onClose={() => {
          /* Keep verification open until saved or the number is edited. */
        }}
      >
        {verification && (
          <VerificationMenu
            mobile={verification.values.mobile}
            verificationToken={verification.token}
            onClose={() => setVerification(null)}
            onSuccess={async (token) => {
              await completeContactDetails(verification.values, token)
              setVerification(null)
            }}
            resendCode={() =>
              startMobileVerification(verification.values.mobile)
            }
          />
        )}
      </SlideOutMenu>
    </>
  )
}

type VerificationMenuProps = {
  mobile: string
  verificationToken: string

  onClose: () => void
  onSuccess: (verificationToken: string) => Promise<void>
  resendCode: () => Promise<string>
}

const otpSchema = object({
  otp: string()
    .default('')
    .required('Please enter the 6-digit code we sent.')
    .matches(/^\d{6}$/, 'Please enter all 6 digits of the code.'),
})

interface OtpFormValues extends InferType<typeof otpSchema> {}

function VerificationMenu({
  mobile,
  verificationToken,
  resendCode,
  onClose,
  onSuccess,
}: VerificationMenuProps) {
  const client = useApiClient()
  const verifyTfaMutation = useMutation(VerifyTfaChallenge.mutation(client))

  const [lastSent, setLastSent] = useState(new Date())
  const [_, setNow] = useState(new Date())
  const [resendingCode, setResendingCode] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [challengeToken, setChallengeToken] = useState(verificationToken)
  const [verifiedToken, setVerifiedToken] = useState('')
  const [codeSent, setCodeSent] = useState(false)

  const methods = useForm({
    reValidateMode: 'onChange',
    criteriaMode: 'firstError',
    mode: 'onTouched',
    defaultValues: otpSchema.cast({}),
    resolver: yupResolver(otpSchema),
  })

  const { handleSubmit, formState, control } = methods
  const { isSubmitting } = formState
  const isLoading = resendingCode || isSubmitting

  useInterval(() => {
    setNow(new Date())
  }, 500)

  async function onClickResend() {
    if (isLoading) return
    setResendingCode(true)
    setErrorMessage('')
    setCodeSent(false)
    try {
      const token = await resendCode()
      setChallengeToken(token)
      setVerifiedToken('')
      methods.reset({ otp: '' })
      setLastSent(new Date())
      setCodeSent(true)
      methods.setFocus('otp')
    } catch (error) {
      setErrorMessage(smsRequestError(error))
    } finally {
      setResendingCode(false)
    }
  }

  async function onSubmit(values: OtpFormValues) {
    if (resendingCode || !challengeToken) return
    setErrorMessage('')
    setCodeSent(false)
    let token = verifiedToken
    if (!token) {
      try {
        const result = await verifyTfaMutation.mutateAsync({
          data: {
            token: challengeToken,
            code: values.otp,
            channel: 'Sms',
            address: mobile,
            intent: 'PROFILE_UPDATE',
          },
        })
        token = result.token
        setVerifiedToken(token)
      } catch (error) {
        if (isHttpError(error) && error.response.error === 'TFA_FAILURE') {
          setErrorMessage(
            'That code is incorrect. Check the code or request a new one.',
          )
        } else if (
          !isHttpError(error) ||
          requiresMobileVerification(error) ||
          error.response.error === 'TWILIO_VERIFY_RATE_LIMIT_REACHED'
        ) {
          // A missing/expired challenge, or an unreadable response after it may
          // have been consumed, must not be replayed by Continue.
          setChallengeToken('')
          setErrorMessage(
            !isHttpError(error)
              ? "We couldn't verify your code. Please use Resend code to request a new one."
              : error.response.error === 'TWILIO_VERIFY_RATE_LIMIT_REACHED'
                ? 'Too many verification attempts. Please wait 10 minutes, then use Resend code to request a new code.'
                : 'This code has expired or is no longer available. Please use Resend code to request a new one.',
          )
        } else {
          setErrorMessage(
            "We couldn't verify your code. Check your connection and try Continue again, or use Resend code to request a new one.",
          )
        }
        return
      }
    }
    try {
      await onSuccess(token)
    } catch (error) {
      if (requiresMobileVerification(error)) {
        setVerifiedToken('')
        setChallengeToken('')
        setErrorMessage(
          'Your verification has expired. Please request a new code.',
        )
      } else {
        setErrorMessage(
          "Your mobile number is verified, but we couldn't save your details. Please try Continue again.",
        )
      }
    }
  }

  const diff = differenceInSeconds(new Date(), lastSent)
  const wait = 14.5
  const secondsToNextSend = Math.ceil(Math.max(wait - diff, 0))

  return (
    <div className="mx-auto max-h-[75dvh] max-w-3xl overflow-y-auto px-6 sm:px-8 lg:px-10">
      <FormProvider {...methods}>
        <form
          className="space-y-9 sm:space-y-10 lg:space-y-11"
          noValidate
          onSubmit={handleSubmit(onSubmit, () =>
            setErrorMessage('Please enter all 6 digits of the code.'),
          )}
        >
          <div className="space-y-3 sm:space-y-4">
            <h3 className="text-xl font-medium leading-tight tracking-tight text-gray-900 sm:text-2xl">
              Enter the 6-digit code we sent via SMS to{' '}
              <span className="text-nowrap break-keep">
                {formatMobileNumber(mobile)}
              </span>
              .
            </h3>
            <button
              type="button"
              disabled={isLoading}
              className="text-base text-gray-900 underline transition-all hover:text-gray-600"
              onClick={() => onClose()}
            >
              Not your mobile number?
            </button>
          </div>
          <div className="space-y-2 sm:space-y-3">
            <Controller
              name="otp"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <OTPInput
                  {...field}
                  autoFocus
                  maxLength={6}
                  aria-label="SMS verification code"
                  aria-invalid={!!errorMessage}
                  aria-describedby={
                    errorMessage ? 'sms-verification-error' : undefined
                  }
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  containerClassName="group flex items-center has-[:disabled]:opacity-30"
                  disabled={isLoading || !!verifiedToken || !challengeToken}
                  render={({ slots }) => (
                    <>
                      <div className="flex w-full max-w-sm gap-x-2">
                        {slots.map((slot, idx) => (
                          <Slot
                            key={idx}
                            {...slot}
                            disabled={verifyTfaMutation.isPending}
                          />
                        ))}
                      </div>
                    </>
                  )}
                />
              )}
            />

            {errorMessage && (
              <div
                id="sms-verification-error"
                role="alert"
                className="relative mt-1 block pl-4 text-sm text-red-600"
              >
                <FontAwesomeIcon
                  icon={faCircleExclamation}
                  className="mr-1.5"
                />
                {errorMessage}
              </div>
            )}
          </div>

          <div className="space-y-4 sm:space-y-5">
            {codeSent && (
              <p role="status" className="text-sm text-gray-700">
                A new code has been sent.
              </p>
            )}
            <p className="text-base tracking-tight text-gray-900">
              Didn{"'"}t get an SMS?{' '}
              {secondsToNextSend > 0 ? (
                <button type="button" disabled className="inline text-gray-400">
                  Resend code in {secondsToNextSend}.
                </button>
              ) : (
                <button
                  type="button"
                  disabled={isLoading}
                  className="text-gray-900 underline hover:text-gray-600"
                  onClick={() => onClickResend()}
                >
                  {resendingCode ? 'Sending code…' : 'Resend code.'}
                </button>
              )}
            </p>
            <RoundedButton
              type="submit"
              aria-label="Continue"
              aria-busy={isSubmitting}
              loading={isSubmitting}
              disabled={isLoading || !challengeToken}
            >
              Continue
            </RoundedButton>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}

type AugmentedSlotProps = SlotProps & {
  disabled?: boolean
}

function Slot(props: AugmentedSlotProps) {
  return (
    <div
      className={clsx(
        'relative h-12 min-w-0 flex-1 text-[1.5rem] sm:h-14',
        'flex items-center justify-center',
        'transition-all duration-300',
        'rounded-lg border border-gray-300',
        'group-focus-within:border-gray-400 group-hover:border-gray-400',
        'outline outline-0 outline-gray-600/40',
        { 'outline-2 outline-blue-600': props.isActive },
        props.disabled && 'bg-gray-400',
      )}
    >
      <div className="text-gray-900 group-has-[input[data-input-otp-placeholder-shown]]:opacity-20">
        {props.char ?? props.placeholderChar}
      </div>
      {props.hasFakeCaret && <FakeCaret />}
    </div>
  )
}

// You can emulate a fake textbox caret!
function FakeCaret() {
  return (
    <div className="pointer-events-none absolute inset-0 flex animate-caret-blink items-center justify-center">
      <div className="h-8 w-px bg-black" />
    </div>
  )
}

function requiresMobileVerification(error: unknown): boolean {
  return (
    isHttpError(error) &&
    ['INVALID_TOKEN', 'TFA_REQUIRED'].includes(error.response.error)
  )
}

function smsRequestError(error: unknown): string {
  if (
    isHttpError(error) &&
    (error.status === 429 ||
      error.response.error === 'TWILIO_VERIFY_RATE_LIMIT_REACHED')
  ) {
    return 'Too many codes have been requested. Please wait 10 minutes before trying again.'
  }
  return "We couldn't send your verification code. Check your mobile number and connection, then try again."
}
